import * as React from "react";
import {
  makeStyles,
  Text,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Badge,
  Spinner,
  Card,
  Avatar,
  Button,
  Input,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Label,
  Dropdown,
  Option,
} from "@fluentui/react-components";
import {
  PeopleRegular,
  EditRegular,
  SearchRegular,
  ShieldDismissRegular,
  ShieldCheckmarkRegular,
  ChevronLeftRegular,
  ChevronRightRegular,
  DeleteRegular,
} from "@fluentui/react-icons";
import * as adminApi from "../../services/adminApi";
import type { AdminUser } from "../../services/adminApi";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "28px",
    height: "100%",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  title: {
    fontSize: "24px",
    fontWeight: "700",
    color: "#1e293b",
    letterSpacing: "-0.5px",
  },
  searchBox: {
    width: "280px",
  },
  card: {
    padding: "0",
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    boxShadow:
      "0 1px 3px rgba(0, 0, 0, 0.04), 0 4px 12px rgba(0, 0, 0, 0.03)",
    border: "1px solid #f1f5f9",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    padding: "64px",
    backgroundColor: "#ffffff",
  },
  emptyState: {
    textAlign: "center",
    padding: "64px",
    color: "#94a3b8",
  },
  emptyIcon: {
    fontSize: "48px",
    marginBottom: "16px",
    color: "#cbd5e1",
  },
  tableContainer: {
    overflowX: "auto",
    width: "100%",
  },
  table: {
    minWidth: "auto",
    width: "100%",
    borderCollapse: "collapse",
    tableLayout: "fixed",
  },
  tableHeader: {
    backgroundColor: "#f8fafc",
    borderBottom: "1px solid #f1f5f9",
  },
  tableHeaderCell: {
    fontSize: "12px",
    fontWeight: "600",
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    padding: "8px 12px",
    whiteSpace: "nowrap",
  },
  tableRow: {
    transitionProperty: "background-color",
    transitionDuration: "0.1s",
    "&:hover": {
      backgroundColor: "#f8fafc",
    },
  },
  tableCell: {
    padding: "8px 12px",
    fontSize: "13px",
    color: "#334155",
    borderBottom: "1px solid #f1f5f9",
    verticalAlign: "middle",
  },
  userCell: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  userName: {
    fontWeight: "600",
    color: "#1e293b",
    fontSize: "13px",
  },
  editButton: {
    minWidth: "auto",
    padding: "4px 8px",
  },
  dialogContent: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    paddingTop: "16px",
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  statsRow: {
    display: "flex",
    gap: "16px",
    flexWrap: "wrap",
  },
  statCard: {
    flex: 1,
    minWidth: "120px",
    padding: "16px 20px",
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    border: "1px solid #f1f5f9",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
  },
  statLabel: {
    fontSize: "12px",
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  statValue: {
    fontSize: "24px",
    fontWeight: "700",
    color: "#1e293b",
    marginTop: "4px",
  },
  tableFooter: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "10px 16px",
    borderTop: "1px solid #f1f5f9",
    backgroundColor: "#f8fafc",
    fontSize: "13px",
    color: "#64748b",
  },
  pagination: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  pageButton: {
    minWidth: "auto",
    padding: "4px 8px",
  },
  dangerButton: {
    backgroundColor: "#dc2626",
    color: "#ffffff",
    "&:hover": {
      backgroundColor: "#b91c1c",
    },
  },
  dangerText: {
    color: "#dc2626",
    fontWeight: "600",
  },
});

interface EditFormState {
  userId: string;
  plan: string;
  credits_remaining: number;
  credits_total: number;
  bonus_credits: number;
  expiry_date: string;
}

export const UserListPage: React.FC = () => {
  const styles = useStyles();
  const [users, setUsers] = React.useState<AdminUser[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [search, setSearch] = React.useState("");
  const [stats, setStats] = React.useState<adminApi.StatsResponse | null>(null);
  const [page, setPage] = React.useState(1);
  const [totalUsers, setTotalUsers] = React.useState(0);
  const PAGE_SIZE = 100;

  // Edit dialog
  const [isEditOpen, setIsEditOpen] = React.useState(false);
  const [editUser, setEditUser] = React.useState<AdminUser | null>(null);
  const [editForm, setEditForm] = React.useState<EditFormState | null>(null);
  const [saving, setSaving] = React.useState(false);

  // Danger confirmation dialog
  const [dangerDialog, setDangerDialog] = React.useState<{
    open: boolean;
    action: "ban" | "unban" | "delete";
    user: AdminUser | null;
    loading: boolean;
  }>({ open: false, action: "ban", user: null, loading: false });

  const loadData = React.useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, statsRes] = await Promise.all([
        adminApi.getUsers({ page, limit: PAGE_SIZE, search: search || undefined }),
        adminApi.getStats(),
      ]);
      setUsers(usersRes.users);
      setTotalUsers(usersRes.total);
      setStats(statsRes);
    } catch (e) {
      console.error("Failed to load data:", e);
    } finally {
      setLoading(false);
    }
  }, [search, page]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  // Debounced search
  const searchTimerRef = React.useRef<ReturnType<typeof setTimeout>>();
  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => loadData(), 400);
  };

  const planBadge = (plan: string) => {
    const config: Record<string, { color: "warning" | "informative" | "success"; label: string }> = {
      free: { color: "warning", label: "Free" },
      lite: { color: "informative", label: "Lite" },
      plus: { color: "success", label: "Plus" },
    };
    const c = config[plan] || config.free;
    return (
      <Badge appearance="filled" color={c.color} size="small">
        {c.label}
      </Badge>
    );
  };

  const openEdit = (user: AdminUser) => {
    setEditUser(user);
    setEditForm({
      userId: user.id,
      plan: user.plan,
      credits_remaining: user.credits_remaining,
      credits_total: user.credits_total,
      bonus_credits: user.bonus_credits,
      expiry_date: user.expiry_date
        ? new Date(user.expiry_date).toISOString().split("T")[0]
        : "",
    });
    setIsEditOpen(true);
  };

  const openDangerDialog = (user: AdminUser, action: "ban" | "unban" | "delete") => {
    setDangerDialog({ open: true, action, user, loading: false });
  };

  const handleDangerConfirm = async () => {
    const { action, user } = dangerDialog;
    if (!user) return;
    setDangerDialog((d) => ({ ...d, loading: true }));
    try {
      if (action === "ban") {
        await adminApi.banUser(user.id);
      } else if (action === "unban") {
        await adminApi.unbanUser(user.id);
      } else {
        await adminApi.deleteUser(user.id);
      }
      setDangerDialog({ open: false, action: "ban", user: null, loading: false });
      loadData();
    } catch (e) {
      console.error(`Failed to ${action} user:`, e);
      setDangerDialog((d) => ({ ...d, loading: false }));
    }
  };

  const handleSave = async () => {
    if (!editForm || !editUser) return;
    setSaving(true);
    try {
      await adminApi.updateUser(editUser.id, {
        plan: editForm.plan,
        credits_remaining: editForm.credits_remaining,
        credits_total: editForm.credits_total,
        bonus_credits: editForm.bonus_credits,
        expiry_date: editForm.expiry_date
          ? new Date(editForm.expiry_date).toISOString()
          : null,
      });
      setIsEditOpen(false);
      loadData();
    } catch (e) {
      console.error("Failed to save:", e);
      alert("Failed to update user");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.container}>
      {/* Stats */}
      {stats && (
        <div className={styles.statsRow}>
          <div className={styles.statCard}>
            <Text className={styles.statLabel}>Total Users</Text>
            <Text className={styles.statValue} block>
              {stats.total}
            </Text>
          </div>
          <div className={styles.statCard}>
            <Text className={styles.statLabel}>Free</Text>
            <Text className={styles.statValue} block>
              {stats.free}
            </Text>
          </div>
          <div className={styles.statCard}>
            <Text className={styles.statLabel}>Lite</Text>
            <Text className={styles.statValue} block>
              {stats.lite}
            </Text>
          </div>
          <div className={styles.statCard}>
            <Text className={styles.statLabel}>Plus</Text>
            <Text className={styles.statValue} block>
              {stats.plus}
            </Text>
          </div>
        </div>
      )}

      {/* Header */}
      <div className={styles.header}>
        <Text className={styles.title}>Users</Text>
        <Input
          className={styles.searchBox}
          placeholder="Search by name or email..."
          contentBefore={<SearchRegular />}
          value={search}
          onChange={(_, data) => handleSearchChange(data.value)}
        />
      </div>

      {/* Table */}
      <Card className={styles.card}>
        {loading ? (
          <div className={styles.loadingContainer}>
            <Spinner size="medium" label="Loading users..." />
          </div>
        ) : users.length === 0 ? (
          <div className={styles.emptyState}>
            <PeopleRegular className={styles.emptyIcon} />
            <Text style={{ display: "block" }}>No users found</Text>
          </div>
        ) : (
          <div className={styles.tableContainer}>
            <Table className={styles.table}>
              <TableHeader className={styles.tableHeader}>
                <TableRow>
                  <TableHeaderCell className={styles.tableHeaderCell} style={{ width: "15%" }}>
                    Name
                  </TableHeaderCell>
                  <TableHeaderCell className={styles.tableHeaderCell} style={{ width: "22%" }}>
                    Email
                  </TableHeaderCell>
                  <TableHeaderCell className={styles.tableHeaderCell} style={{ width: "8%" }}>
                    Plan
                  </TableHeaderCell>
                  <TableHeaderCell className={styles.tableHeaderCell} style={{ width: "14%" }}>
                    Credits
                  </TableHeaderCell>
                  <TableHeaderCell className={styles.tableHeaderCell} style={{ width: "10%" }}>
                    Expiry
                  </TableHeaderCell>
                  <TableHeaderCell className={styles.tableHeaderCell} style={{ width: "8%" }}>
                    Status
                  </TableHeaderCell>
                  <TableHeaderCell className={styles.tableHeaderCell} style={{ width: "12%" }}>
                    Registered
                  </TableHeaderCell>
                  <TableHeaderCell className={styles.tableHeaderCell} style={{ width: "11%" }}>
                    Actions
                  </TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} className={styles.tableRow}>
                    <TableCell className={styles.tableCell}>
                      <div className={styles.userCell}>
                        <Avatar
                          name={user.name || user.email}
                          size={24}
                          color="colorful"
                        />
                        <Text className={styles.userName}>
                          {user.name || user.email.split("@")[0]}
                        </Text>
                      </div>
                    </TableCell>
                    <TableCell
                      className={styles.tableCell}
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "200px",
                      }}
                    >
                      {user.email}
                    </TableCell>
                    <TableCell className={styles.tableCell}>
                      {planBadge(user.plan)}
                    </TableCell>
                    <TableCell className={styles.tableCell}>
                      {user.credits_remaining} / {user.credits_total}
                      {user.bonus_credits > 0 && (
                        <span style={{ color: "#4ECBA5", marginLeft: "4px" }}>
                          +{user.bonus_credits}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className={styles.tableCell}>
                      {user.expiry_date
                        ? new Date(user.expiry_date).toLocaleDateString()
                        : <span style={{ color: "#cbd5e1" }}>-</span>}
                    </TableCell>
                    <TableCell className={styles.tableCell}>
                      {user.banned_until && new Date(user.banned_until) > new Date() ? (
                        <Badge appearance="filled" color="danger" size="small">
                          Banned
                        </Badge>
                      ) : (
                        <Badge appearance="filled" color="success" size="small">
                          Active
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className={styles.tableCell}>
                      {new Date(user.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className={styles.tableCell}>
                      <div style={{ display: "flex", gap: "4px" }}>
                        <Button
                          icon={<EditRegular />}
                          size="small"
                          className={styles.editButton}
                          onClick={() => openEdit(user)}
                          title="Edit user"
                        />
                        <Button
                          icon={user.banned_until && new Date(user.banned_until) > new Date()
                            ? <ShieldCheckmarkRegular />
                            : <ShieldDismissRegular />}
                          size="small"
                          className={styles.editButton}
                          onClick={() => {
                            const isBanned = user.banned_until && new Date(user.banned_until) > new Date();
                            openDangerDialog(user, isBanned ? "unban" : "ban");
                          }}
                          title={user.banned_until && new Date(user.banned_until) > new Date()
                            ? "Unban user"
                            : "Ban user"}
                        />
                        <Button
                          icon={<DeleteRegular />}
                          size="small"
                          className={styles.editButton}
                          onClick={() => openDangerDialog(user, "delete")}
                          title="Delete user"
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className={styles.tableFooter}>
              <Text size={200} style={{ color: "#64748b" }}>
                {totalUsers} users total
              </Text>
              {totalUsers > PAGE_SIZE && (
                <div className={styles.pagination}>
                  <Button
                    icon={<ChevronLeftRegular />}
                    size="small"
                    className={styles.pageButton}
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                  />
                  <Text size={200}>
                    {page} / {Math.ceil(totalUsers / PAGE_SIZE)}
                  </Text>
                  <Button
                    icon={<ChevronRightRegular />}
                    size="small"
                    className={styles.pageButton}
                    disabled={page >= Math.ceil(totalUsers / PAGE_SIZE)}
                    onClick={() => setPage((p) => p + 1)}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </Card>

      {/* Danger Confirmation Dialog */}
      <Dialog
        open={dangerDialog.open}
        onOpenChange={(_, data) => {
          if (!data.open) setDangerDialog((d) => ({ ...d, open: false }));
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>
              {dangerDialog.action === "delete"
                ? "Delete User"
                : dangerDialog.action === "ban"
                  ? "Ban User"
                  : "Unban User"}
            </DialogTitle>
            <DialogContent>
              {dangerDialog.action === "delete" ? (
                <Text>
                  Are you sure you want to <span style={{ color: "#dc2626", fontWeight: 600 }}>permanently delete</span>{" "}
                  <Text weight="semibold">{dangerDialog.user?.email}</Text>?
                  This action cannot be undone. All user data will be removed.
                </Text>
              ) : dangerDialog.action === "ban" ? (
                <Text>
                  Are you sure you want to <span style={{ color: "#dc2626", fontWeight: 600 }}>ban</span>{" "}
                  <Text weight="semibold">{dangerDialog.user?.email}</Text>?
                  The user will no longer be able to sign in.
                </Text>
              ) : (
                <Text>
                  Are you sure you want to unban{" "}
                  <Text weight="semibold">{dangerDialog.user?.email}</Text>?
                </Text>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => setDangerDialog((d) => ({ ...d, open: false }))}
                disabled={dangerDialog.loading}
              >
                Cancel
              </Button>
              <Button
                style={
                  dangerDialog.action === "unban"
                    ? undefined
                    : { backgroundColor: "#dc2626", color: "#fff" }
                }
                appearance={dangerDialog.action === "unban" ? "primary" : "primary"}
                onClick={handleDangerConfirm}
                disabled={dangerDialog.loading}
              >
                {dangerDialog.loading
                  ? "Processing..."
                  : dangerDialog.action === "delete"
                    ? "Delete"
                    : dangerDialog.action === "ban"
                      ? "Ban"
                      : "Unban"}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={isEditOpen}
        onOpenChange={(_, data) => setIsEditOpen(data.open)}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Edit User</DialogTitle>
            <DialogContent className={styles.dialogContent}>
              {editUser && editForm && (
                <>
                  <div style={{ marginBottom: "8px" }}>
                    <Text weight="semibold">
                      {editUser.name || editUser.email}
                    </Text>
                    <Text
                      size={200}
                      block
                      style={{ color: "#64748b" }}
                    >
                      {editUser.email}
                    </Text>
                  </div>

                  <div className={styles.formGroup}>
                    <Label>Plan</Label>
                    <Dropdown
                      value={
                        editForm.plan === "free"
                          ? "Free"
                          : editForm.plan === "lite"
                            ? "Lite"
                            : "Plus"
                      }
                      selectedOptions={[editForm.plan]}
                      onOptionSelect={(_, data) => {
                        if (data.optionValue) {
                          setEditForm({
                            ...editForm,
                            plan: data.optionValue,
                          });
                        }
                      }}
                    >
                      <Option value="free">Free</Option>
                      <Option value="lite">Lite</Option>
                      <Option value="plus">Plus</Option>
                    </Dropdown>
                  </div>

                  <div className={styles.formGroup}>
                    <Label>Credits Remaining</Label>
                    <Input
                      type="number"
                      value={String(editForm.credits_remaining)}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          credits_remaining: parseInt(e.target.value) || 0,
                        })
                      }
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <Label>Credits Total</Label>
                    <Input
                      type="number"
                      value={String(editForm.credits_total)}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          credits_total: parseInt(e.target.value) || 0,
                        })
                      }
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <Label>Bonus Credits</Label>
                    <Input
                      type="number"
                      value={String(editForm.bonus_credits)}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          bonus_credits: parseInt(e.target.value) || 0,
                        })
                      }
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <Label>Expiry Date</Label>
                    <Input
                      type="date"
                      value={editForm.expiry_date}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          expiry_date: e.target.value,
                        })
                      }
                    />
                  </div>
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => setIsEditOpen(false)}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save"}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};
